import { useCallback, useRef } from 'react';

/**
 * Returns an `enqueue` function that serializes async operations.
 * Each call to `enqueue(fn)` waits for the previous operation to
 * settle before invoking `fn`. A failed operation does not block
 * subsequent ones.
 *
 * The returned promise for each call resolves/rejects with the
 * result of `fn`, so callers can still handle individual errors.
 */
const useSerializedQueue = () => {
    const chainRef = useRef<Promise<any>>(Promise.resolve());

    const enqueue = useCallback(<T>(fn: () => Promise<T>): Promise<T> => {
        const promise = chainRef.current.then(() => fn());
        // Swallow errors on the chain so a failed operation doesn't block the next one
        chainRef.current = promise.catch(() => {});
        return promise;
    }, []);

    return enqueue;
};

export default useSerializedQueue;
